import { NavLink, useNavigate } from 'react-router-dom';
import { SystemHealthAlert } from './SystemHealthPanel';
import { BackupAlertBanner } from './BackupAlertBanner';
import { RattachementAlert } from './RattachementAnomalies';
import { useAuth } from '../context/AuthContext';
import {
    LayoutDashboard, Receipt, ShieldAlert,
    Wallet, Settings, LogOut, Menu, X, Landmark, Clock, FileText, ClipboardCheck, Calculator, Sparkles, ShieldCheck,
    ScanSearch,
} from "lucide-react";
import { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../lib/api';

const NAV_ITEMS = [
    { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, roles: ['super_admin', 'admin', 'admin_agence', 'user'] },
    { to: '/commissions', label: 'Commissions', icon: Receipt, roles: ['super_admin', 'admin', 'admin_agence', 'user'] },
    { to: '/sav', label: 'SAV', icon: ShieldAlert, roles: ['super_admin', 'admin', 'admin_agence', 'assistant'] },
    { to: '/frais-bureau', label: 'Frais de bureau', icon: Wallet, roles: ['super_admin', 'admin', 'admin_agence'] },
    { to: '/frais-vehicules', label: 'Frais véhicules', icon: FileText, roles: ['super_admin', 'admin', 'admin_agence', 'assistant'] },
    { to: '/controle-factures', label: 'Contrôle factures', icon: ScanSearch, roles: ['super_admin', 'admin', 'admin_agence', 'responsable', 'assistant'] },
    { to: '/controle-cg', label: 'Contrôle CG', icon: ClipboardCheck, roles: ['super_admin', 'admin', 'admin_agence'] },
    { to: '/tva', label: 'TVA', icon: Calculator, roles: ['super_admin', 'admin', 'admin_agence'] },
    { to: '/optimisation', label: 'Optimisation IA', icon: Sparkles, roles: ['super_admin', 'admin', 'admin_agence'] },
    { to: '/coherence', label: 'Cohérence', icon: ShieldCheck, roles: ['super_admin'] },
    { to: '/charges', label: 'Charges fixes', icon: Landmark, roles: ['super_admin', 'admin', 'admin_agence'] },
    { to: '/admin', label: 'Administration', icon: Settings, roles: ['super_admin', 'admin', 'admin_agence'] },
];

export default function AppLayout({ children }) {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [trial, setTrial] = useState(null);
    const visibleItems = NAV_ITEMS.filter(item => item.roles.includes(user?.role || 'user'));
    const handleLogout = async () => { await logout(); navigate('/landing'); };
    const isAdmin = ['super_admin', 'admin', 'admin_agence'].includes(user?.role);

    useEffect(() => {
        if (isAdmin) {
            axios.get(`${API_URL}/api/agency/trial-status`, { withCredentials: true })
                .then(res => setTrial(res.data))
                .catch(() => {});
        }
    }, [isAdmin]);

    return (
        <div className="min-h-screen flex bg-[#F5F7FA]">
            {sidebarOpen && <div className="fixed inset-0 bg-black/20 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />}

            <aside className={`fixed inset-y-0 left-0 z-50 w-[220px] flex flex-col bg-[#1F2937] transition-transform lg:translate-x-0 lg:static ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
                <div className="flex items-center gap-2.5 px-5 h-14 border-b border-white/10">
                    <div className="flex h-7 w-7 items-center justify-center rounded-md bg-[#6366F1]">
                        <span className="text-xs font-bold text-white font-['Inter']">M</span>
                    </div>
                    <span className="text-[15px] font-semibold font-['Inter'] text-white tracking-tight">Marge Auto</span>
                    <button onClick={() => setSidebarOpen(false)} className="ml-auto lg:hidden text-gray-400"><X className="w-4 h-4" /></button>
                </div>

                <nav className="flex-1 px-2.5 py-3 space-y-px overflow-y-auto" data-testid="sidebar-nav">
                    {visibleItems.map(item => {
                        const Icon = item.icon;
                        return (
                            <NavLink key={item.to} to={item.to} onClick={() => setSidebarOpen(false)} data-testid={`nav-${item.to.slice(1)}`}
                                className={({ isActive }) =>
                                    `flex items-center gap-2.5 px-3 py-[9px] rounded-lg text-[13px] font-medium transition-all duration-150 ${
                                        isActive
                                            ? 'bg-white/[0.12] text-white'
                                            : 'text-gray-400 hover:text-white hover:bg-white/[0.06]'
                                    }`
                                }>
                                <Icon className="w-4 h-4 shrink-0" strokeWidth={1.7} />
                                {item.label}
                            </NavLink>
                        );
                    })}
                </nav>

                <div className="px-2.5 py-3 border-t border-white/10">
                    {trial?.trial && isAdmin && (
                        <div className="mx-3 mb-3 px-3 py-2 rounded-lg bg-white/[0.05] border border-white/10" data-testid="trial-badge">
                            <div className="flex items-center gap-1.5">
                                <Clock className="w-3 h-3 text-[#818CF8]" />
                                <span className="text-[11px] text-[#818CF8] font-medium">Essai gratuit</span>
                            </div>
                            {trial.expired ? (
                                <p className="text-[10px] text-gray-400 mt-1">Période d'essai terminée.</p>
                            ) : (
                                <p className="text-[10px] text-gray-400 mt-1">Il vous reste {trial.days_left} jour{trial.days_left > 1 ? 's' : ''}.</p>
                            )}
                        </div>
                    )}
                    <div className="px-3 mb-2">
                        <p className="text-[13px] font-medium text-gray-200 truncate">{user?.name}</p>
                        <p className="text-[11px] text-gray-500 truncate">{user?.agency_name || user?.role}</p>
                    </div>
                    <button onClick={handleLogout} data-testid="sidebar-logout-btn"
                        className="flex items-center gap-2 w-full px-3 py-[7px] rounded-lg text-[13px] text-gray-400 hover:text-white hover:bg-white/[0.06] transition-colors">
                        <LogOut className="w-4 h-4" strokeWidth={1.7} />Déconnexion
                    </button>
                </div>
            </aside>

            <div className="flex-1 flex flex-col min-w-0">
                <header className="lg:hidden sticky top-0 z-30 bg-white/80 backdrop-blur-sm border-b border-gray-200 px-4 h-12 flex items-center gap-3">
                    <button onClick={() => setSidebarOpen(true)} className="text-gray-400 hover:text-gray-700" data-testid="mobile-menu-btn"><Menu className="w-5 h-5" /></button>
                    <span className="text-sm font-semibold font-['Inter'] text-gray-800">Marge Auto</span>
                </header>
                <main className="flex-1"><BackupAlertBanner /><RattachementAlert />{user?.role === 'super_admin' && <SystemHealthAlert />}{children}</main>
            </div>
        </div>
    );
}
